import React, { useContext } from 'react';
import { Flex, Toggle } from 'components';
import { H5 } from 'components/typography';
import { useTranslation } from 'react-i18next';
import { hasPermission } from 'lane-shared/helpers';
import { PERMISSION_KEYS } from 'constants-permissions';
import { UserDataContext } from 'lane-shared/contexts';
import { usePMTaskAutoExpirationEnabled } from 'react-hooks';
import type { ClientPmSchedule } from 'graphql-query-contracts';

interface TaskExpirationSectionProps {
  channelId?: string;
  autoExpireTask: boolean;
  onScheduleFieldChange: (
    field: keyof ClientPmSchedule,
    value: string | boolean
  ) => void;
}

export const TaskExpirationSection: React.FC<TaskExpirationSectionProps> = ({
  channelId,
  autoExpireTask,
  onScheduleFieldChange,
}) => {
  const { t } = useTranslation();
  const { user } = useContext(UserDataContext);

  const hasPMTaskExpirationPermission =
    user?.isSuperUser ||
    hasPermission(
      user?.roles,
      [PERMISSION_KEYS.PERMISSION_WORK_ORDERS_PM_SCHEDULE_CREATE],
      channelId,
      false
    );

  const isPMTaskAutoExpirationFeatureEnabled = usePMTaskAutoExpirationEnabled();

  const canAccessPMTaskAutoExpiration =
    hasPMTaskExpirationPermission && isPMTaskAutoExpirationFeatureEnabled;

  if (!canAccessPMTaskAutoExpiration) {
    return null;
  }

  return (
    <Flex direction="column" gap={2} className="mt-6">
      <H5>
        {t(
          'web.admin.workOrder.preventiveMaintenance.schedule.Form.taskExpiration'
        )}
      </H5>
      <Toggle
        testId="expire-missed-tasks-toggle"
        value={autoExpireTask}
        onChange={(value: boolean) =>
          onScheduleFieldChange('autoExpireTask', value)
        }
        text={t(
          'web.admin.workOrder.preventiveMaintenance.schedule.Form.expireMissedTasks'
        )}
        description={t(
          'web.admin.workOrder.preventiveMaintenance.schedule.Form.expireMissedTasksDescription'
        )}
        doTranslate={false}
      />
    </Flex>
  );
};
